import React from 'react'
import { Button } from '@components/common'
import ActionButtons from '../ActionButtons/ActionButtons.component'
import { NoteGridRow } from './NoteTextEditor.types'
import {
  isLatestNoteInVersionChain,
  type NoteVersionChainMember,
} from '@utils/noteVersion.util'
import { ClipMode } from '@constants/Common/GridComponent.contants'

type NotesGridColumnsArgs = {
  showEditor: boolean
  versionChainPool: NoteGridRow[]
  onView: (props: NoteGridRow) => void
  onEdit: (props: NoteGridRow) => void
  onDelete: (props: NoteGridRow) => void
  onDownload: (props: NoteGridRow) => void
  /** When false, row actions omit delete. Default true. */
  allowDelete?: boolean
}

type NotesGridColumn = {
  field: string
  headerText: string
  width: string
  textAlign?: 'Center'
  clipMode?: ClipMode
  template?: (props: NoteGridRow) => React.ReactNode
}

export const createNotesGridColumns = ({
  showEditor,
  versionChainPool,
  onView,
  onEdit,
  onDelete,
  onDownload,
  allowDelete = true,
}: NotesGridColumnsArgs): NotesGridColumn[] =>
  [
    {
      field: 'note_title',
      headerText: 'Title',
      clipMode: ClipMode.EllipsisWithTooltip,
      width: '68px',
    },
    {
      field: 'date_time_updated_at_text',
      headerText: 'Last updated',
      width: '40px',
    },
    {
      field: 'date_time_created_at_text',
      headerText: 'Date created',
      width: '40px',
    },
    {
      field: 'actions',
      headerText: 'Actions',
      width: '30px',
      textAlign: 'Center' as const,
      template: (props: NoteGridRow) => {
        const pool = versionChainPool as NoteVersionChainMember[]
        const noteForChain = props as NoteVersionChainMember
        const canEditRow =
          !showEditor || isLatestNoteInVersionChain(noteForChain, pool)
        return (
          <div className="notes-actions-column">
            <ActionButtons
              onView={() => onView(props)}
              {...(showEditor && { onEdit: () => onEdit(props) })}
              {...(showEditor &&
                allowDelete && {
                  onDelete: () => onDelete(props),
                })}
              showEdit={showEditor}
              showDelete={false}
              disableEdit={!canEditRow}
              disabledEditTooltip="Only the latest version of this note can be edited"
              showMoreOptions={showEditor}
              customMiddleButton={
                <Button
                  className="act-button act-button-download"
                  onClick={() => onDownload(props)}
                  title="Download"
                >
                  <i className="mgc mgc_download_2_fill" />
                </Button>
              }
            />
          </div>
        )
      },
    },
  ].map((column) => ({ ...column }))
